import { useState } from "react";
import { Link } from "react-router-dom";
import { Users, UserPlus, UserX, ScanLine, Wallet, AlertTriangle, CreditCard, CalendarClock, Award } from "lucide-react";
import { Area, AreaChart, Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Input } from "@/components/ui/input";
import { PageHeader, Kpi, ExportButton, Empty } from "@/components/common";
import { Belt } from "@/components/Belt";
import { useApi } from "@/lib/useApi";
import { brl, dt, monthStart, todayISO } from "@/lib/fmt";

function ChartCard({ title, children, testid }) {
  return (
    <div className="panel p-5 fade-up" data-testid={testid}>
      <div className="font-display text-lg font-bold uppercase mb-3">{title}</div>
      <div className="h-56">{children}</div>
    </div>
  );
}

function ListCard({ title, items, render, empty, testid, link }) {
  return (
    <div className="panel p-5" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <div className="font-display text-lg font-bold uppercase">{title}</div>
        {link && <Link to={link} className="text-xs font-semibold text-red-600 hover:underline">Ver tudo</Link>}
      </div>
      {items?.length ? <div className="divide-y divide-slate-100">{items.map(render)}</div> : <Empty text={empty} />}
    </div>
  );
}

export default function Dashboard() {
  const [start, setStart] = useState(monthStart());
  const [end, setEnd] = useState(todayISO());
  const [d] = useApi(`/dashboard?start=${start}&end=${end}`);
  const f = d?.finance;
  return (
    <div>
      <PageHeader eyebrow="Visão geral" title="Dashboard">
        <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="w-40" data-testid="dash-start-input" />
        <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="w-40" data-testid="dash-end-input" />
        <ExportButton kind="pagamentos" start={start} end={end} label="Pagamentos" />
        <ExportButton kind="frequencia" start={start} end={end} label="Frequência" />
        <ExportButton kind="alunos" label="Alunos" />
      </PageHeader>
      {!d ? <div className="text-sm text-slate-500">Carregando indicadores…</div> : (
        <div className="space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3 sm:gap-4">
            <Kpi testid="kpi-active" label="Alunos ativos" value={d.active_students} hint={`${d.trial_students} em experimental`} icon={Users} />
            <Kpi testid="kpi-new" label="Novas matrículas" value={d.new_enrollments} hint="no período" icon={UserPlus} tone="green" />
            <Kpi testid="kpi-inactive" label="Inativos / plano vencido" value={`${d.inactive_students} / ${d.expired_plans}`} icon={UserX} tone="amber" />
            <Kpi testid="kpi-checkins" label="Check-ins hoje" value={d.checkins_today} hint={`${d.checkins_period} no período`} icon={ScanLine} />
            <Kpi testid="kpi-revenue" label="Receita recebida" value={brl(f.received_total)} hint={`${f.received_count} pagamentos`} icon={Wallet} tone="green" />
            <Kpi testid="kpi-pending" label="A receber (no prazo)" value={brl(f.pending_total)} hint={`${f.pending_count} cobranças`} icon={CalendarClock} />
            <Kpi testid="kpi-overdue" label="Inadimplência" value={brl(f.overdue_total)} hint={`${f.overdue_students} alunos · ${f.delinquency_rate}%`} icon={AlertTriangle} tone="red" />
            <Kpi testid="kpi-subscriptions" label="Assinaturas Stripe" value={f.active_subscriptions} hint="ativas" icon={CreditCard} />
            <Kpi testid="kpi-expiring" label="Planos a vencer" value={d.expiring_plans.length} hint="próximos dias" icon={CalendarClock} tone="amber" />
            <Kpi testid="kpi-eligible" label="Aptos à graduação" value={d.eligible_count} hint={`${d.near_count} próximos`} icon={Award} tone="red" />
          </div>
          <div className="grid lg:grid-cols-3 gap-4">
            <ChartCard title="Evolução de alunos" testid="chart-growth">
              <ResponsiveContainer><AreaChart data={d.growth}><CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="month" tickFormatter={(m) => m.slice(5) + "/" + m.slice(2, 4)} /><YAxis width={30} /><Tooltip />
                <Area dataKey="total" name="Alunos" stroke="#DC2626" fill="#DC2626" fillOpacity={0.15} strokeWidth={2} /></AreaChart></ResponsiveContainer>
            </ChartCard>
            <ChartCard title="Frequência diária" testid="chart-checkins">
              <ResponsiveContainer><BarChart data={d.checkins_series}><CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="date" tickFormatter={(x) => x.slice(8)} /><YAxis width={30} /><Tooltip labelFormatter={dt} />
                <Bar dataKey="count" name="Check-ins" fill="#18181B" radius={[3, 3, 0, 0]} /></BarChart></ResponsiveContainer>
            </ChartCard>
            <ChartCard title="Receita mensal" testid="chart-revenue">
              <ResponsiveContainer><BarChart data={f.monthly}><CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="month" tickFormatter={(m) => m.slice(5)} /><YAxis width={50} /><Tooltip formatter={(v) => brl(v)} />
                <Bar dataKey="total" name="Recebido" fill="#DC2626" radius={[3, 3, 0, 0]} /></BarChart></ResponsiveContainer>
            </ChartCard>
          </div>
          <div className="grid lg:grid-cols-3 gap-4">
            <ListCard title="Aptos à graduação" testid="list-eligible" items={d.eligible} link="/admin/graduacao" empty="Nenhum aluno apto no momento."
              render={(s) => (<Link to={`/admin/alunos/${s.id}`} key={s.id} className="flex items-center justify-between py-2.5 hover:bg-slate-50 -mx-2 px-2 rounded">
                <div><div className="text-sm font-semibold">{s.full_name}</div><div className="text-xs text-slate-500">Próximo: {s.next?.belt} {s.next?.grau ? `${s.next.grau}º grau` : ""}</div></div>
                <Belt belt={s.belt} grau={s.grau} size="sm" /></Link>)} />
            <ListCard title="Planos a vencer" testid="list-expiring" items={d.expiring_plans} empty="Nenhum plano vencendo."
              render={(s) => (<Link to={`/admin/alunos/${s.id}`} key={s.id} className="flex justify-between py-2.5 text-sm hover:bg-slate-50 -mx-2 px-2 rounded">
                <span className="font-semibold">{s.full_name}</span><span className="text-amber-600 font-semibold">{dt(s.plan_end)}</span></Link>)} />
            <ListCard title="Deixando de treinar" testid="list-absent" items={d.absent} link="/admin/frequencia" empty="Todos treinando em dia."
              render={(s) => (<Link to={`/admin/alunos/${s.id}`} key={s.id} className="flex justify-between py-2.5 text-sm hover:bg-slate-50 -mx-2 px-2 rounded">
                <span className="font-semibold">{s.full_name}</span><span className="text-red-600 font-semibold">{s.days_since} dias</span></Link>)} />
          </div>
        </div>
      )}
    </div>
  );
}
